import { useEffect, useRef } from "react"
import type { ContactPayload } from "./model"
import {
  ApiFormWrapper,
  FormError,
  FormInput,
  FormSubmitButton,
  FormTextarea,
} from "@holigral/shared/form"
import { useT } from "@holigral/shared/i18n"

type Props = {
  /** Submits to POST /api/contact, typically an RTK Query mutation trigger */
  action: (payload: ContactPayload) => { unwrap: () => Promise<unknown> }
}

/**
 * The contact form both sites use. The page around it (heading, intro, the
 * address to write to directly) is the site's own.
 */
export default function ContactForm({ action }: Props) {
  const { t } = useT("shared")
  const pageLoadRef = useRef(0)

  useEffect(() => {
    pageLoadRef.current = Math.floor(Date.now() / 1000)
  }, [])

  return (
    <ApiFormWrapper
      defaultValues={{
        name: "",
        email: "",
        company: null,
        message: "",
        website: null,
      }}
      action={(values: Omit<ContactPayload, "t">) =>
        action({
          ...values,
          t: pageLoadRef.current,
        })
      }
      successMessage={t("contact.sent")}
      resetOnSuccess
    >
      <div className="space-y-4">
        <FormInput
          name="name"
          placeholder={t("contact.name")}
          required
          autoFocus
        />
        <FormInput
          name="email"
          type="email"
          autoComplete="email"
          placeholder={t("contact.email")}
          required
        />
        <FormInput name="company" placeholder={t("contact.company")} />
        <FormTextarea
          name="message"
          placeholder={t("contact.message")}
          required
          rows={5}
        />

        {/* Honeypot – hidden from real users */}
        <div className="sr-only" aria-hidden="true">
          <FormInput name="website" tabIndex={-1} autoComplete="off" />
        </div>

        <FormError />
        <FormSubmitButton disabledUntilDirty={false}>
          {t("contact.send")}
        </FormSubmitButton>
      </div>
    </ApiFormWrapper>
  )
}
